import React, { Component } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CustomizedSnackbars from '../../components/material-snackbars';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import UsageVisitAccordion from '../../components/usage/usage-visit-accordion';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import { UsageKpiCard, UsageKindChip, UsageStatusChip, UsageApiChip, UsageDurationBar, UsageEmpty, UsageEllipsisCell, UsageOriginChip, UsageRankBar, UsageBlockTitle, fmtDateTime, fmtMs, fmtInt, kindLabel } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Linha do tempo de UM usuário (investigação). Lê o fato por índice (S1/S11).
// Duas leituras: por VISITA (accordion tela → filhos) e plana (tabela com filtros finos).
// Chega com ?userId=&userName=&start=&end= pelos links do dashboard e das recusas.
// ---------------------------------------------------------------------------

const KINDS = ['', 'read', 'write', 'export', 'login', 'logout', 'screen', 'denied', 'permission_denied', 'role_denied', 'blocked', 'rate_limited', 'would_deny'];
const PAGE_SIZE = 20;

function parseQuery(search) {
  const q = new URLSearchParams(search || '');
  const start = q.get('start'); const end = q.get('end');
  const range = start && end
    ? { preset: 'custom', dateStart: moment(start).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(end).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
    : { preset: '7d', ...presetRange('7d') };
  return { ...range, userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '', api: '', actionGroup: '' };
}
// ?visit=<id>: veio do 'ver a visita' do drawer — modo Por visita com essa visita já aberta.
const parseVisit = (search) => new URLSearchParams(search || '').get('visit') || '';
// ?route=<rota>: veio da aba Endpoints — linha do tempo já filtrada pela rota (contém).
const parseRoute = (search) => new URLSearchParams(search || '').get('route') || '';
const RANK_LIMIT = 10;

// Intervalo desde a chamada anterior (linha de baixo, mais antiga). Curto e repetido = robô.
const fmtDelta = (ms) => (ms === null || ms === undefined ? '—' : ms < 1000 ? `${ms} ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)} s` : ms < 3600000 ? `${Math.round(ms / 60000)} min` : `${(ms / 3600000).toFixed(1)} h`);

export default class UsageUserActivity extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filter: { ...parseQuery(props.location?.search), ip: props.ip || '' },
      actionKind: '', entityType: '', entityId: '', route: parseRoute(props.location?.search),
      rankings: null, rankingsLoading: false,
      expandVisit: parseVisit(props.location?.search),
      mode: 'visits', autoFlat: false, loading: false, loaded: false,
      summary: null, visits: [], rows: [], count: 0, page: 1,
      drawerRow: null, snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.loadVisitRows = this.loadVisitRows.bind(this);
    this.openRow = this.openRow.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.exportCsv = this.exportCsv.bind(this);
    this.onPage = this.onPage.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; if (this.state.filter.userId || this.state.filter.ip || this.state.route) this.load(1); }

  // Montada permanentemente (troca de aba não perde a consulta). Querystring nova com a aba ativa
  // (ranking → linha do tempo) → novos filtros + busca.
  componentDidUpdate(prevProps) {
    // IP vem da área (index.js): um valor para as 4 abas do fato. Mudou em OUTRA aba → aplica aqui e
    // recarrega o que já estava carregado. Mudou NESTA aba, o filtro já está igual (onFilter) e nada acontece.
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }), () => { if (this.state.loaded) this.load(1); });
      return;
    }
    const search = this.props.location?.search || '';
    if (this.props.active && search && search !== this.lastSearch) {
      this.lastSearch = search;
      const expandVisit = parseVisit(search);
      const route = parseRoute(search);
      this.setState((st) => ({ filter: { ...parseQuery(search), ip: this.props.ip || '' }, expandVisit, route, mode: expandVisit ? 'visits' : (route ? 'flat' : st.mode), loaded: false, autoFlat: false, visits: [], rows: [], count: 0, rankings: null }), () => { if (this.state.filter.userId || this.state.filter.ip || this.state.route) this.load(1); });
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) {
    // IP é da área: avisa o index.js (que repassa às outras abas) e segue com o filtro local.
    if ((next.ip || '') !== (this.state.filter.ip || '') && this.props.onIpChange) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  openRow(row) { this.setState({ drawerRow: row }); }
  closeDrawer() { this.setState({ drawerRow: null }); }
  onPage(e, page) { this.load(page); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }

  load(page) {
    const f = this.state.filter;
    if (!f.userId && !f.ip && !this.state.actionKind && !this.state.route.trim()) { this.notify('error', this.tr('usage.ua.needUserOrIp', 'Informe o usuário, o IP, a ação ou a rota.')); return; }
    // Sem usuário (só IP, ação ou rota) não existe visita: a lista É plana. Antes o modo ficava em 'visits' com
    // os dados carregados planos → lista vazia com a paginação da lista plana (vindo do Tráfego por IP).
    const mode = f.userId ? this.state.mode : 'flat';
    this.setState({ loading: true, page, mode, ...(page === 1 ? { rankings: null } : {}) });
    // Sem usuário (só IP) não há resumo consolidado: o daily não tem IP.
    const summary = f.userId ? UsageService.summary({ ...f, screenRoute: '' }) : Promise.resolve({ current: null });
    const list = mode === 'visits' && f.userId
      ? UsageService.visits(f.userId, f.dateStart, f.dateEnd, page, PAGE_SIZE, f.ip).then((r) => ({ visits: r.data, rows: [], count: r.count }))
      : this.flatQuery(page);
    Promise.all([summary, list]).then(([s, l]) => {
      // 'Por visita' só mostra o que passou pelas telas. Sem visita nenhuma (chamadas diretas à API:
      // URL colada, ferramenta, script), cai na lista plana sozinho e avisa — em vez de 'nada'.
      if (mode === 'visits' && f.userId && page === 1 && !l.visits.length) {
        return this.flatQuery(1).then((flat) => {
          if (flat.rows.length) this.setState({ loading: false, loaded: true, summary: s.current, mode: 'flat', autoFlat: true, ...flat });
          else this.setState({ loading: false, loaded: true, summary: s.current, ...l });
        });
      }
      this.setState({ loading: false, loaded: true, summary: s.current, ...l });
      return null;
    }).catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  flatParams(page) {
    const f = this.state.filter;
    return { userId: f.userId, dateStart: f.dateStart, dateEnd: f.dateEnd, page, pageSize: PAGE_SIZE, actionKind: this.state.actionKind, entityType: this.state.entityType, entityId: this.state.entityId, ip: f.ip, routeLike: this.state.route.trim() || undefined };
  }

  // Rankings do mesmo recorte (atalhos): pedidos junto com a página 1 da lista plana.
  loadRankings() {
    this.setState({ rankingsLoading: true });
    UsageService.activityRankings({ ...this.flatParams(1), limit: RANK_LIMIT })
      .then((rankings) => this.setState({ rankings, rankingsLoading: false }))
      .catch(() => this.setState({ rankingsLoading: false }));
  }

  flatQuery(page) {
    if (page === 1) this.loadRankings();
    return UsageService.activity(this.flatParams(page))
      .then((r) => ({ visits: [], rows: r.data, count: r.count }));
  }

  loadVisitRows(id) { return UsageService.visitRows(id); }

  exportCsv() {
    const f = this.state.filter;
    if (!f.userId && !f.ip) { this.notify('error', this.tr('usage.ua.needUserOrIp', 'Informe o usuário ou o IP.')); return; }
    this.setState({ loading: true });
    UsageService.exportActivity({
      dateStart: moment(f.dateStart).format('YYYY-MM-DD'), dateEnd: moment(f.dateEnd).format('YYYY-MM-DD'),
      userId: f.userId || null, ip: f.ip || null, actionKind: this.state.actionKind || null, entityType: this.state.entityType || null, entityId: this.state.entityId ? Number(this.state.entityId) : null,
    }).then((r) => {
      this.setState({ loading: false });
      if (r && r.success) this.notify('success', this.tr('usage.ua.exportOk', 'Exportação solicitada. O arquivo chega por e-mail em alguns minutos.'));
      else this.notify('error', (r && r.errors && r.errors[0]) || this.tr('usage.ua.exportFail', 'Falha ao solicitar a exportação.'));
    }).catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  renderSummary() {
    const { intl } = this.props;
    const s = this.state.summary; if (!s) return null;
    const cards = [
      { title: this.tr('usage.ua.kpi.requests', 'Requests'), value: s.reads + s.writes + s.exports, color: '#3F51B4' },
      { title: this.tr('usage.col.screens', 'Telas'), value: s.screens, color: '#9C27B0' },
      { title: this.tr('usage.col.writes', 'Escritas'), value: s.writes, color: '#8BC34A' },
      { title: this.tr('usage.col.exports', 'Exportações'), value: s.exports, color: '#FF9800' },
      { title: this.tr('usage.col.denials', 'Recusas'), value: s.denials, color: '#F44336' },
      { title: this.tr('usage.dash.kpi.errors', 'Erros 5xx'), value: s.errors, color: '#B71C1C' },
      { title: this.tr('usage.ua.kpi.avg', 'Duração média'), value: s.avgDurationMs, format: fmtMs, color: '#607D8B' },
      { title: this.tr('usage.ua.kpi.max', 'Máximo'), value: s.maxDurationMs, format: fmtMs, color: '#607D8B' },
    ];
    return <Grid container spacing={2} style={{ marginBottom: 12 }}>{cards.map((c) => <Grid item xs={6} sm={3} lg={1.5} key={c.title}><UsageKpiCard intl={intl} {...c} /></Grid>)}</Grid>;
  }

  // Clique num ranking = aplicar o filtro e buscar de novo (usuário e IP são do filtro comum; rota é o campo local).
  pickUser(it) { const id = Number(it.key) || 0; if (!id) return; this.onFilter({ ...this.state.filter, userId: id, userName: it.label && !it.label.startsWith('#') ? it.label : '' }); this.setState({ autoFlat: false }, () => this.load(1)); }
  pickIp(it) { if (!it.key) return; this.onFilter({ ...this.state.filter, ip: it.key }); this.setState({ autoFlat: false }, () => this.load(1)); }
  pickRoute(it) { if (!it.key) return; this.setState({ route: it.key, autoFlat: false }, () => this.load(1)); }

  renderRankings() {
    const { intl } = this.props;
    const { rankings, rankingsLoading, filter } = this.state;
    if (rankingsLoading) return <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={20} color="secondary" /></Box>;
    if (!rankings) return null;
    const blocks = [];
    if (!filter.userId) blocks.push({ id: 'users', title: this.tr('usage.ua.rank.users', 'Usuários com mais eventos'), items: rankings.byUser, color: '#009688', onClick: (it) => this.pickUser(it) });
    if (!filter.ip) blocks.push({ id: 'ips', title: this.tr('usage.ua.rank.ips', 'IPs com mais eventos'), items: rankings.byIp, color: '#3F51B4', onClick: (it) => this.pickIp(it) });
    blocks.push({ id: 'routes', title: this.tr('usage.ua.rank.routes', 'Rotas mais chamadas'), items: rankings.byRoute, color: '#FF9800', onClick: (it) => this.pickRoute(it) });
    if (!blocks.some((b) => b.items.length)) return null;
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginBottom: 12 }}><CardContent style={{ paddingBottom: 8 }}>
        <UsageBlockTitle title={this.tr('usage.ua.rank.title', 'Resumo do resultado')} subtitle={this.tr('usage.ua.rank.subtitle', 'os 10 maiores do recorte atual · clique para filtrar por ele')} />
        <Grid container spacing={2}>
          {blocks.map((b) => (
            <Grid item xs={12} md={12 / blocks.length} key={b.id}>
              <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 4 }}>{b.title}</Typography>
              <UsageRankBar intl={intl} items={b.items} labelKey="label" valueKey="total" color={b.color} onClick={b.onClick} />
            </Grid>
          ))}
        </Grid>
      </CardContent></Card>
    );
  }

  renderFlat() {
    const { intl } = this.props;
    const { rows, filter } = this.state;
    const showUser = !filter.userId; // busca por IP: cada linha diz de quem foi
    const anon = this.tr('usage.anonymous', 'anônimo');
    if (!rows.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.ua.noEvents', 'Nenhum evento com esses filtros')} />;
    const maxMs = Math.max(1000, ...rows.map((r) => Number(r.durationMs) || 0));
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell align="right"><Tooltip title={this.tr('usage.ua.delta.tip', 'Tempo desde a chamada anterior (linha de baixo). Sequência de intervalos curtos e iguais é robô.')}><span>{this.tr('usage.col.delta', 'Δ anterior')}</span></Tooltip></TableCell>{showUser && <TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell>}<TableCell>{this.tr('usage.col.action', 'Ação')}</TableCell><TableCell>API</TableCell><TableCell>{this.tr('usage.col.routeOrScreen', 'Rota / tela')}</TableCell>
            <TableCell>{this.tr('usage.col.origin', 'Origem')}</TableCell><TableCell>{this.tr('usage.col.entity', 'Entidade')}</TableCell><TableCell>Status</TableCell><TableCell>{this.tr('usage.col.duration', 'Duração')}</TableCell><TableCell>IP</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r, i) => {
              const prev = rows[i + 1];
              const delta = prev ? Math.max(0, new Date(r.occurredAt) - new Date(prev.occurredAt)) : null;
              return (
              <TableRow key={r.id} hover style={{ cursor: 'pointer', background: r.actionKind === 'screen' ? '#faf5ff' : undefined }} onClick={() => this.openRow(r)}>
                <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt, true)}</TableCell>
                <TableCell align="right" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: delta !== null && delta < 1000 ? '#c62828' : delta !== null && delta < 5000 ? '#ef6c00' : '#666' }}>{fmtDelta(delta)}</TableCell>
                {showUser && <TableCell><UsageEllipsisCell text={r.userName || (r.userId ? `#${r.userId}` : anon)} maxWidth={180} /></TableCell>}
                <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                <TableCell><UsageApiChip api={r.api} /></TableCell>
                <TableCell><UsageEllipsisCell text={r.actionKind === 'screen' ? r.path : r.route} title={r.url} maxWidth={280} /></TableCell>
                <TableCell><UsageOriginChip intl={intl} row={r} maxWidth={200} /></TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{r.entityType}{r.entityId ? <b> #{r.entityId}</b> : ''}</TableCell>
                <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                <TableCell><UsageDurationBar ms={r.durationMs} max={maxMs} /></TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}{r.details && r.details.forwarded ? <span style={{ color: '#c62828' }}> ⚠</span> : null}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, mode, autoFlat, loading, loaded, visits, count, page, drawerRow, snack, actionKind, entityType, entityId, route, expandVisit } = this.state;
    const actions = (
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8, marginTop: 10 }}>
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && this.setState({ mode: v, autoFlat: false }, () => loaded && this.load(1))}>
            <ToggleButton value="visits" disabled={!filter.userId}>{this.tr('usage.ua.mode.visits', 'Por visita')}</ToggleButton><ToggleButton value="flat">{this.tr('usage.ua.mode.flat', 'Plana')}</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" color="secondary" onClick={() => this.setState({ autoFlat: false }, () => this.load(1))} disabled={loading} startIcon={<PersonSearchIcon />}>{this.tr('usage.ua.search', 'Buscar')}</Button>
          <Tooltip title={this.tr('usage.ua.exportTooltip', 'Exportação assíncrona: o CSV chega por e-mail')}><span><Button variant="outlined" onClick={this.exportCsv} disabled={loading || (!filter.userId && !filter.ip)} startIcon={<FileDownloadIcon />}>{this.tr('usage.ua.export', 'Exportar')}</Button></span></Tooltip>
          {/* Voltar pela história do React Router (recusas/dashboard → linha do tempo → origem). Sem história, vai ao dashboard. Mesmo padrão de entity-access. */}
          <Button variant="outlined" color="primary" startIcon={<ArrowBackIcon />}
            onClick={() => (this.props.history.length > 1 ? this.props.history.goBack() : this.props.history.push(`/${this.props.lang}/usage/dashboard`))}>
            {this.tr('back', 'Voltar')}
          </Button>
        </Box>
    );
    return (
      <Box>
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showApi={false} showGroup={false} />
        {(mode === 'flat' || !filter.userId) && (
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={4} md={2}>
              <TextField select fullWidth size="small" label={this.tr('usage.ua.action', 'Ação')} value={actionKind} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }} onChange={(e) => this.setState({ actionKind: e.target.value })}>
                {KINDS.map((k) => <option key={k} value={k}>{k ? kindLabel(intl, k) : this.tr('usage.ua.action.all', 'Todas')}</option>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" label={this.tr('usage.ua.entityType', 'Entidade (ex.: called)')} value={entityType} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ entityType: e.target.value })} /></Grid>
            <Grid item xs={12} sm={4} md={2}><TextField fullWidth size="small" type="number" label={this.tr('usage.ua.entityId', 'Id da entidade')} value={entityId} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ entityId: e.target.value })} /></Grid>
            {/* Rota = Controller.Action da API (ex.: GetFileDownloadLink). "Contém", sem distinguir caixa: quem acessou um endpoint, quando e de onde. */}
            <Grid item xs={12} sm={6} md={3}><TextField fullWidth size="small" label={this.tr('usage.ua.route', 'Rota / endpoint (contém)')} placeholder="GetFileDownloadLink" value={route} InputLabelProps={{ shrink: true }} onChange={(e) => this.setState({ route: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') this.setState({ autoFlat: false }, () => this.load(1)); }} /></Grid>
          </Grid>
        )}
        {actions}

        <Box style={{ marginTop: 16 }}>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
          {!loading && loaded && this.renderSummary()}
          {!loading && loaded && !filter.userId && !filter.ip && <Alert severity="info" style={{ marginBottom: 12 }}>{this.tr('usage.ua.actionOnly', 'Busca sem usuário nem IP: todas as ocorrências da ação/rota no período (até 90 dias), de todos os usuários. A coluna Usuário diz quem fez e a coluna IP de onde.')}</Alert>}
          {!loading && loaded && autoFlat && <Alert severity="info" style={{ marginBottom: 12 }}>{this.tr('usage.ua.autoFlat', 'Este usuário não tem visitas de tela no período. Os eventos abaixo vieram de chamadas diretas à API (URL colada no navegador, ferramenta ou script), sem passar pelas telas do sistema. Exibindo a lista plana.')}</Alert>}
          {!loading && loaded && mode === 'visits' && expandVisit && visits.length > 0 && !visits.some((v) => v.screenVisitId === expandVisit) && <Alert severity="info" style={{ marginBottom: 12 }}>{this.tr('usage.ua.visitNotOnPage', 'A visita pedida não está nesta página. Use a paginação abaixo.')}</Alert>}
          {!loading && loaded && mode === 'visits' && (visits.length ? visits.map((v) => <UsageVisitAccordion key={v.screenVisitId} intl={intl} visit={v} defaultExpanded={v.screenVisitId === expandVisit} loadRows={this.loadVisitRows} onRowClick={this.openRow} />) : <UsageEmpty intl={intl} compact title={this.tr('usage.ua.noVisits', 'Nenhuma tela aberta no período')} />)}
          {!loading && loaded && mode === 'flat' && this.renderRankings()}
          {!loading && loaded && mode === 'flat' && <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12 }}><CardContent style={{ padding: 0 }}>{this.renderFlat()}</CardContent></Card>}
          {!loading && !loaded && <UsageEmpty intl={intl} icon={<PersonSearchIcon style={{ fontSize: 56 }} />} title={this.tr('usage.ua.empty.title', 'Escolha um usuário, um IP, uma ação ou uma rota, e um período')} description={this.tr('usage.ua.empty.description', 'A linha do tempo mostra cada tela aberta e, dentro dela, cada chamada à API, na ordem em que aconteceram.')} />}
          {!loading && loaded && count > PAGE_SIZE && (
            <Box display="flex" alignItems="center" style={{ marginTop: 8, gap: 12 }}>
              <Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} />
              <Typography variant="caption" style={{ color: '#888' }}>{fmtInt(count)} {mode === 'visits' ? this.tr('usage.ua.visits', 'visita(s)') : this.tr('usage.ua.events', 'evento(s)')}</Typography>
            </Box>
          )}
        </Box>
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={this.closeDrawer} lang={this.props.lang} history={this.props.history} onCopy={() => this.notify('success', this.tr('usage.copied', 'Copiado!'))} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
    );
  }
}
